import { useGetOrders, useGetSalesChannels } from '@agos/api-client/orders'
import { useGetSettingsGeneral } from '@agos/api-client/platform'
import { filterChannelsOf, preorderLeadMinutes, startOfDay } from '@agos/shared'
import { useMemo } from 'react'

import { AWAITING_ACCEPTANCE, awaitingAcceptance, type BoardOrder } from './board'

/**
 * How often the count re-asks - the board's own cadence (`DispatchPage`'s `REFRESH_SECONDS`), so the shell's
 * number is never staler than the board it points at.
 */
export const UNACCEPTED_POLL_MS = 10_000

/**
 * The board's page size, so a busy day's pending list is read whole rather than its first page - the server
 * refuses anything above 500 by reverting the whole query to today (`OrderController.readFilter`).
 */
const PENDING_PAGE_SIZE = '500'

/**
 * How many orders are waiting on *Rozvoz - nástenka* to be accepted - §9.674, owner O58.
 *
 * **For the shell rather than the board.** The owner: *"keď si prekliknuty v kuchyni vieš že ti stoja obj na
 * rozvoz nástenka"* - a manager working on another screen has to be able to see that web orders are waiting.
 * `AppLayout` draws the number; this is the read and the rule, kept in the module that owns the board so the
 * count is the board's own band (`awaitingAcceptance`) rather than a second spelling of it.
 *
 * **`enabled` is the caller's answer to *may this person open the board*.** A cook without `dispatch` makes no
 * request at all, and every query here is switched off with it.
 *
 * **The day's pending orders, counted through the board's membership.** `status=pending` narrows the list on
 * the server; what the server cannot know is the franchise's preorder lead time or which channels are the road,
 * so the count is taken after `boardOrders` has applied both - see `awaitingAcceptance`. The channels and the
 * settings are the board's own cached reads, so on a board user's device they usually cost nothing.
 *
 * **`now` is when the read answered** (`dataUpdatedAt`), not a clock read during render: a preorder coming due
 * is counted at the next poll, which is when the board would draw it too, and the render stays pure.
 *
 * Answers nought until the orders read has answered, so a badge never flashes a number taken from nothing.
 */
export function useUnacceptedCount(enabled: boolean): number {
  const today = startOfDay(new Date()).toISOString()
  const { data: result, dataUpdatedAt } = useGetOrders(
    { status: AWAITING_ACCEPTANCE, date_from: today, page_size: PENDING_PAGE_SIZE },
    { query: { refetchInterval: UNACCEPTED_POLL_MS, enabled } },
  )
  const { data: salesChannels } = useGetSalesChannels({ query: { enabled } })
  const { data: settings } = useGetSettingsGeneral({ query: { enabled } })

  return useMemo(() => {
    if (!enabled || result === undefined) return 0
    const pending = (result.data ?? []) as BoardOrder[]
    return awaitingAcceptance(
      pending,
      filterChannelsOf(salesChannels),
      new Date(dataUpdatedAt),
      preorderLeadMinutes(settings),
    ).length
  }, [enabled, result, salesChannels, settings, dataUpdatedAt])
}
